import { Fragment, useCallback } from "react";
import EnumWalletTypes from "@wallet-manager/node-types/dist/types/postgres/const/WalletType";
import AddressBookSuggestion from "src/components/AddressBookSuggestion";
import ChainNameSingleSelection from "src/components/ContractSweepRecipientSetting/ChainNameSingleSelection";
import { SelectChangeEvent } from "src/components/MuiGenerals";
import RiskTypeSingleSelection from "src/components/RiskTypeSingleSelection";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import { genField } from "src/utils/HelperComp";
import { AddressBookOrder } from "../../../Swap/types";
import { FilterFields, FilterFieldsKeys } from "../types/FilterFields";
import { TableColumnKey } from "../types/Table";
import {
  AddressBookFilterType,
  createAddressBookFilterConfig,
} from "src/utils/AddressBookFilter/createAddressBookFilter";
import { SingleSelection } from "src/components/GeneralSelection";
import { SweepDestStatus } from "@wallet-manager/node-types/dist/src/postgres/const";
import CurrencySingleSelection from "src/components/ContractSweepRecipientSetting/CurrencySingleSelection";

const useRenderFilters = () => {
  const { translatePrefix, fields, setFields } = usePageConfigProviderContext<
    FilterFields,
    unknown
  >();
  const { t, tc, translate } = useTranslation(translatePrefix);

  const ADDRESS_BOOK_SUGGESTION_FILTER_CONFIG = [
    createAddressBookFilterConfig(AddressBookFilterType.ContractHotWallet, {
      t: translate,
    }),
    createAddressBookFilterConfig(AddressBookFilterType.SweepDestWallet, {
      t: translate,
    }),
  ];

  const updateWalletAddress = useCallback(
    (name: keyof AddressBookOrder, value: string) => {
      if (name === "to_address" || name === "rawItem") {
        return setFields((prevFields: any) => ({
          ...prevFields,
          ...(name === "to_address" && {
            ownerAddress: value,
            addressBookOrder: {
              ...prevFields.addressBookOrder,
              [name]: value,
            },
            ...(value === "" ? { ownerAddress: "", rawWalletInfo: null } : {}),
          }),
          ...(name === "rawItem" && {
            rawWalletInfo: value,
          }),
        }));
      }
    },
    [setFields],
  );

  const filterFields = genField({ t }, [
    [
      TableColumnKey.ChainName,
      <ChainNameSingleSelection
        fieldName={t(TableColumnKey.ChainName)}
        value={fields[FilterFieldsKeys.ChainName]}
        onChange={(e) =>
          setFields((f) => ({ ...f, chainName: e.target.value }))
        }
      />,
    ],
    [
      TableColumnKey.Currency,
      <CurrencySingleSelection
        fieldName={t(TableColumnKey.Currency)}
        onChange={(e) =>
          setFields((fields: any) => ({
            ...fields,
            assetName: e.target.value,
          }))
        }
        value={fields[FilterFieldsKeys.AssetName]}
        chainName={fields.chainName}
      />,
    ],
    [
      TableColumnKey.Status,
      <SingleSelection
        label={tc("phSelection", { fieldName: t(TableColumnKey.Status) })}
        value={fields[FilterFieldsKeys.Status]}
        onChange={(e) =>
          setFields((f) => ({
            ...f,
            status: e.target.value,
          }))
        }
        enumData={{
          Enable: SweepDestStatus.Enable.toString(),
          Disable: SweepDestStatus.Disable.toString(),
        }}
        nameFn={(name) => translate(`enumAutoSweepDestStatus.${name}`)}
        clearSelect={() => {}}
      />,
    ],
    [
      TableColumnKey.RecipientAddress,
      <AddressBookSuggestion
        item={fields[FilterFieldsKeys.AddressBookOrder]}
        isAllowFreeInput={true}
        isDisabled={false}
        isHideAllWallet
        filterConfigs={ADDRESS_BOOK_SUGGESTION_FILTER_CONFIG}
        walletTypes={[String(EnumWalletTypes.SweepDestWallet)]}
        chain_name={fields.chainName || ""}
        setField={updateWalletAddress}
        defaultTabId={AddressBookFilterType.ContractHotWallet}
        config={{
          placeHolder: tc("phInputField", {
            fieldName: t(TableColumnKey.RecipientAddress),
          }),
        }}
      />,
    ],
    [
      TableColumnKey.RiskType,
      <RiskTypeSingleSelection
        value={fields[FilterFieldsKeys.RiskType]}
        onChange={(e: SelectChangeEvent<string>) =>
          setFields((fields) => ({ ...fields, riskType: e.target.value }))
        }
        onClearSelect={() => {}}
      />,
    ],
  ]);

  const renderFields = () =>
    Object.keys(filterFields).map((key, index) => (
      <Fragment key={index}>{filterFields[key]}</Fragment>
    ));

  return { renderFields };
};
export default useRenderFilters;
